// @ts-check
import { ArrowDownLeft, ArrowUpRight, Check, Paperclip } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { formatDateTime, formatDay, label, WHOLESALE_CHANNEL } from '../../utils/wholesale';
import FileUpload from '../FileUpload';
import { card, EnumSelect, Field, input, primaryBtn, secondaryBtn } from './shared';

const blank = (channel = 'email') => ({
  direction: 'outbound',
  channel,
  contact_id: '',
  template_name: '',
  subject: '',
  body: '',
  follow_up_on: '',
  attachments: [],
});

/** @param {{partner: any, onChanged: () => void}} props */
const TimelineTab = ({ partner, onChanged }) => {
  const toast = useToast();
  const [touchpoints, setTouchpoints] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(() => blank(partner.contacts[0]?.preferred_channel));
  const [saving, setSaving] = useState(false);
  const [suggestedDate, setSuggestedDate] = useState(null);
  const [suggestedStage, setSuggestedStage] = useState(null);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const load = useCallback(async () => {
    try {
      setTouchpoints(await adminWholesaleService.listTouchpoints(partner.id, search.trim() || undefined));
    } catch (err) {
      toast.error(err.message || 'Failed to load the timeline');
    }
  }, [partner.id, search, toast]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  useEffect(() => {
    adminWholesaleService.listTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  // Look for a date in the pasted note and offer it; it is only ever filled in when the user taps it.
  useEffect(() => {
    if (form.follow_up_on || form.body.trim().length < 6) {
      setSuggestedDate(null);
      return undefined;
    }
    let live = true;
    const timer = setTimeout(() => {
      adminWholesaleService
        .parseFollowUp(form.body)
        .then((r) => live && setSuggestedDate(r.follow_up_on))
        .catch(() => live && setSuggestedDate(null));
    }, 500);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [form.body, form.follow_up_on]);

  const applyTemplate = async (name) => {
    set({ template_name: name });
    if (!name) return;
    try {
      const contact = form.contact_id || partner.contacts[0]?.id;
      const out = await adminWholesaleService.renderTemplate(partner.id, { template_name: name, contact_id: contact || undefined });
      // An email template is sent as an email whatever the contact prefers; a DM goes on their preferred channel.
      const channel = name.endsWith('_email') ? 'email' : out.channel;
      set({ template_name: name, subject: name.endsWith('_dm') ? '' : out.subject, body: out.body, direction: 'outbound', ...(channel ? { channel } : {}) });
    } catch (err) {
      toast.error(err.message || 'Could not fill the template');
    }
  };

  const save = async () => {
    if (!form.subject.trim() && !form.body.trim()) return toast.error('Paste the message or give it a subject');
    setSaving(true);
    try {
      const saved = await adminWholesaleService.logTouchpoint(partner.id, {
        direction: form.direction,
        channel: form.channel,
        contact_id: form.contact_id || null,
        subject: form.subject.trim() || null,
        body: form.body.trim() || null,
        template_key: form.template_name || null,
        attachments: form.attachments,
        follow_up_on: form.follow_up_on || null,
      });
      toast.success('Logged');
      setSuggestedStage(saved.suggested_stage);
      setForm(blank(form.channel));
      await load();
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Failed to log the message');
    } finally {
      setSaving(false);
    }
  };

  const confirmStage = async () => {
    try {
      await adminWholesaleService.changeStage(partner.id, suggestedStage);
      toast.success(`Moved to ${label(suggestedStage)}`);
      setSuggestedStage(null);
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Could not change the stage');
    }
  };

  const toggleFollowUp = async (t) => {
    try {
      await adminWholesaleService.updateTouchpoint(t.id, { follow_up_done: !t.follow_up_done_at });
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to update the follow-up');
    }
  };

  return (
    <div className="space-y-6">
      {suggestedStage && (
        <div className="flex flex-wrap items-center gap-3 p-4 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 rounded-lg">
          <p className="text-sm flex-1">
            Move {partner.name} to <strong>{label(suggestedStage)}</strong>? Nothing moves unless you confirm.
          </p>
          <button onClick={confirmStage} className={primaryBtn}>
            Move to {label(suggestedStage)}
          </button>
          <button onClick={() => setSuggestedStage(null)} className={secondaryBtn}>
            Not now
          </button>
        </div>
      )}

      <div className={`${card} p-5 space-y-3`}>
        <h2 className="text-base font-semibold">Log a message</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Field label="Direction">
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              {['outbound', 'inbound'].map((d) => (
                <button
                  key={d}
                  onClick={() => set({ direction: d })}
                  aria-pressed={form.direction === d}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-md ${form.direction === d ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}
                >
                  {d === 'outbound' ? 'We sent' : 'They sent'}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Channel">
            <EnumSelect value={form.channel} onChange={(channel) => set({ channel })} options={Object.values(WHOLESALE_CHANNEL)} />
          </Field>
          <Field label="Contact">
            <select className={input} value={form.contact_id} onChange={(e) => set({ contact_id: e.target.value })}>
              <option value="">Not specific</option>
              {partner.contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.email || c.instagram || 'Contact'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Start from a template">
            <select className={input} value={form.template_name} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">Paste a message instead</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>
                  {label(t.name.replace('wholesale_', ''))}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Subject">
          <input className={input} value={form.subject} onChange={(e) => set({ subject: e.target.value })} />
        </Field>
        <Field label={form.direction === 'outbound' ? 'Message sent' : 'Their message (pasted)'}>
          <textarea className={input} rows={7} value={form.body} onChange={(e) => set({ body: e.target.value })} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Follow up on" hint="Becomes a reminder on that day">
            <input type="date" className={input} value={form.follow_up_on} onChange={(e) => set({ follow_up_on: e.target.value })} />
            {suggestedDate && (
              <button onClick={() => set({ follow_up_on: suggestedDate })} className="mt-1 text-xs underline text-muted-foreground hover:text-foreground">
                The note mentions {formatDay(suggestedDate)}. Use it as the follow-up date?
              </button>
            )}
          </Field>
          <Field label="Attachments" hint="PDFs sent or received: linesheet, terms sheet, quote">
            <FileUpload
              key={touchpoints.length}
              uploadType="vendor-document"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              maxFiles={5}
              onUploadComplete={(files) => set({ attachments: [...form.attachments, ...files.map((f) => ({ name: f.name, url: f.url, key: f.public_id }))] })}
            />
          </Field>
        </div>
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Logging...' : 'Log message'}
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 gap-3">
          <h2 className="text-base font-semibold">Timeline</h2>
          <input className={`${input} !w-64`} placeholder="Search messages" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {touchpoints.length === 0 ? (
          <div className={`${card} p-8 text-center text-sm text-muted-foreground`}>{search ? 'No message matches.' : 'Nothing logged yet.'}</div>
        ) : (
          <ul className="space-y-3">
            {touchpoints.map((t) => (
              <li key={t.id} className={`${card} p-4`}>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  {t.direction === 'outbound' ? <ArrowUpRight size={14} /> : <ArrowDownLeft size={14} className="text-green-600" />}
                  <span className="font-medium text-foreground">{t.direction === 'outbound' ? 'We sent' : 'They replied'}</span>
                  <span>· {label(t.channel)}</span>
                  <span>· {formatDateTime(t.occurred_at)}</span>
                  {t.template_key && <span>· {label(t.template_key.replace('wholesale_', ''))}</span>}
                </div>
                {t.subject && <p className="text-sm font-medium">{t.subject}</p>}
                {t.body && <p className="text-sm whitespace-pre-wrap break-words mt-1">{t.body}</p>}
                {t.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {t.attachments.map((a) => (
                      <a key={a.url} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs px-2 py-1 border border-border rounded-md hover:bg-accent">
                        <Paperclip size={12} /> {a.name}
                      </a>
                    ))}
                  </div>
                )}
                {t.follow_up_on && (
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    <span className={t.follow_up_done_at ? 'text-muted-foreground line-through' : 'text-amber-700 dark:text-amber-400'}>
                      Follow up {formatDay(t.follow_up_on)}
                    </span>
                    <button onClick={() => toggleFollowUp(t)} className="flex items-center gap-1 px-2 py-0.5 border border-input rounded-md hover:bg-accent">
                      <Check size={12} /> {t.follow_up_done_at ? 'Reopen' : 'Done'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TimelineTab;
